import { parseClipboardTsv, parseNumericCell, type EstimatingEditableField } from './estimatingClipboard';

export type DirtyBoqPatch = {
  quantity?: number;
  unit_rate?: number;
  description?: string;
  notes?: string;
  uom?: string;
};

export type PastePreviewIssue = {
  rowId: string;
  field: string;
  message: string;
};

export type PastePreviewResult = {
  patches: Map<string, DirtyBoqPatch>;
  issues: PastePreviewIssue[];
};

/** Apply TSV paste starting at focus row / field across visible flat rows. */
export function previewPasteOntoBoqRows(
  rows: Array<{ id: string }>,
  startRowIndex: number,
  startField: EstimatingEditableField,
  clipboardText: string,
  fieldOrder: EstimatingEditableField[]
): PastePreviewResult {
  const matrix = parseClipboardTsv(clipboardText);
  const patches = new Map<string, DirtyBoqPatch>();
  const issues: PastePreviewIssue[] = [];
  const startFieldIdx = Math.max(0, fieldOrder.indexOf(startField));

  matrix.forEach((cols, ri) => {
    const row = rows[startRowIndex + ri];
    if (!row) {
      issues.push({ rowId: '', field: '', message: `Paste exceeds grid at row offset ${ri}` });
      return;
    }
    const patch: DirtyBoqPatch = { ...(patches.get(row.id) || {}) };
    cols.forEach((cell, ci) => {
      const field = fieldOrder[startFieldIdx + ci];
      if (!field) {
        issues.push({
          rowId: row.id,
          field: `col${ci}`,
          message: 'Extra paste column ignored',
        });
        return;
      }
      if (field === 'quantity' || field === 'unit_rate') {
        const parsed = parseNumericCell(cell);
        if (!parsed.ok) {
          issues.push({ rowId: row.id, field, message: parsed.error });
          return;
        }
        if (field === 'quantity') patch.quantity = parsed.value;
        else patch.unit_rate = parsed.value;
      } else if (field === 'description') {
        patch.description = String(cell ?? '');
      } else if (field === 'notes') {
        patch.notes = String(cell ?? '');
      } else if (field === 'uom') {
        patch.uom = String(cell ?? '').trim();
      }
    });
    if (Object.keys(patch).length) patches.set(row.id, patch);
  });

  return { patches, issues };
}
